/**
 * AuthHubService — Integration with DDD Auth Hub
 *
 * Provides SSO (Single Sign-On) capabilities by integrating with the
 * centralised DDD Authentication Hub.
 *
 * Token exchange signs in to BOTH Firebase apps when possible:
 *   1. Hub token → authHubAuth (ddd-auth-hub-4ed14). This is the session the
 *      hub's getAppState/saveAppState callables authenticate with — without
 *      it, the per-user data thin layer (hubAppState.js) cannot sync.
 *   2. Spoke token → auth (this app's own project), when the hub supplies
 *      one. The hub only mints a spoke token for apps registered in its
 *      SPOKE_CONFIG; finding-your-voice is not registered, so real SSO flows
 *      currently produce a hub session only — that alone signs the user in.
 */

import { auth, authHubAuth } from './firebase';
import { signInWithCustomToken, signOut as firebaseSignOut } from 'firebase/auth';
import { getUrlSecret, clearUrlSecrets, HUB_URL_PARAMS } from '../utils/urlSecrets.js';

class AuthHubService {
  constructor() {
    this.hubUrl = import.meta.env.VITE_AUTH_HUB_URL || 'https://auth.divedeepdialogues.com';
    this.appId = 'finding-your-voice';
    this.appName = 'Finding Your Voice';
  }

  isEnabled() {
    return import.meta.env.VITE_USE_AUTH_HUB === 'true';
  }

  redirectToLogin(returnPath = null) {
    const returnUrl = this.buildReturnUrl(returnPath);
    const authUrl = `${this.hubUrl}/login?app=${this.appId}&return=${encodeURIComponent(returnUrl)}`;
    this.storeReturnPath(returnPath);
    window.location.href = authUrl;
  }

  redirectToSignup(returnPath = null) {
    const returnUrl = this.buildReturnUrl(returnPath);
    const authUrl = `${this.hubUrl}/signup?app=${this.appId}&return=${encodeURIComponent(returnUrl)}`;
    this.storeReturnPath(returnPath);
    window.location.href = authUrl;
  }

  redirectToPasswordReset() {
    const returnUrl = this.buildReturnUrl('/signin');
    const authUrl = `${this.hubUrl}/reset-password?app=${this.appId}&return=${encodeURIComponent(returnUrl)}`;
    window.location.href = authUrl;
  }

  redirectToProfile() {
    const returnUrl = this.buildReturnUrl('/profile');
    const authUrl = `${this.hubUrl}/profile?app=${this.appId}&return=${encodeURIComponent(returnUrl)}`;
    window.location.href = authUrl;
  }

  buildReturnUrl(path = null) {
    const baseUrl = window.location.origin;
    const tokenExchangePath = '/auth/token-exchange';
    return `${baseUrl}${tokenExchangePath}${path ? `?redirect=${encodeURIComponent(path)}` : ''}`;
  }

  storeReturnPath(path) {
    if (path) {
      sessionStorage.setItem('authReturnPath', path);
    }
  }

  getAndClearReturnPath() {
    const path = sessionStorage.getItem('authReturnPath');
    sessionStorage.removeItem('authReturnPath');
    return path || '/dashboard';
  }

  /**
   * Tokens may ride the URL fragment (preferred — never hits logs) or query,
   * but main.jsx lifts them into memory before first render
   * (utils/urlSecrets.js), so nothing else sees them. The URL reads are a
   * fallback only.
   */
  _getFragmentParams() {
    return new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
  }

  hasAuthToken() {
    return !!this.getAuthToken();
  }

  /** Hub-audience token — fragment first, query string as fallback. */
  getAuthToken() {
    const captured = getUrlSecret('token');
    if (captured) return captured;
    const fragment = this._getFragmentParams().get('token');
    if (fragment) return fragment;
    return new URLSearchParams(window.location.search).get('token');
  }

  /** Spoke-audience token (this app's own project), when the hub minted one. */
  getSpokeToken() {
    const captured = getUrlSecret('spokeToken');
    if (captured) return captured;
    const fragment = this._getFragmentParams().get('spokeToken');
    if (fragment) return fragment;
    return new URLSearchParams(window.location.search).get('spokeToken');
  }

  getRedirectPath() {
    const params = new URLSearchParams(window.location.search);
    return params.get('redirect') || this.getAndClearReturnPath();
  }

  async handleTokenExchange() {
    const token = this.getAuthToken();
    if (!token) throw new Error('No auth token provided');
    const spokeToken = this.getSpokeToken();
    // Tokens are in hand: get them out of the address bar now, not after the
    // network round trips (and on failure too), then forget them once used.
    this.cleanUrl();
    try {
      return await this._exchange(token, spokeToken);
    } finally {
      clearUrlSecrets(HUB_URL_PARAMS);
    }
  }

  async _exchange(token, spokeToken) {
    // 1. Hub session — required by the hub data callables. Non-fatal if the
    //    own-project session below succeeds instead.
    let hubCredential = null;
    if (authHubAuth) {
      try {
        hubCredential = await signInWithCustomToken(authHubAuth, token);
      } catch (error) {
        console.warn('[AuthHubService] Hub session sign-in failed:', error?.code || error);
      }
    }

    // 2. Own-project session. With no spoke token, signing the hub token
    //    into the own project throws auth/custom-token-mismatch — expected
    //    while finding-your-voice has no spoke registration; the hub session
    //    then carries the sign-in alone.
    let spokeCredential = null;
    if (auth) {
      try {
        spokeCredential = await signInWithCustomToken(auth, spokeToken || token);
      } catch (error) {
        if (!hubCredential) {
          console.error('[AuthHubService] Token exchange failed:', error);
          throw error;
        }
        console.warn(
          '[AuthHubService] Own-project sign-in unavailable, continuing on the hub session:',
          error?.code || error
        );
      }
    }

    const credential = spokeCredential || hubCredential;
    if (!credential) {
      throw new Error('No auth session could be established');
    }

    const user = credential.user;
    const idTokenResult = await user.getIdTokenResult();
    const claims = idTokenResult.claims;
    const isNewUser = user.metadata.creationTime === user.metadata.lastSignInTime;
    return { user, claims, isNewUser };
  }

  cleanUrl() {
    const url = new URL(window.location.href);
    const fragment = this._getFragmentParams();
    if (!fragment.has('token') && !fragment.has('spokeToken')
      && !url.searchParams.has('token') && !url.searchParams.has('spokeToken')) return;
    url.searchParams.delete('token');
    url.searchParams.delete('spokeToken');
    url.searchParams.delete('app');
    url.hash = '';
    window.history.replaceState({}, '', url.toString());
  }

  async signOut(redirectToHub = false) {
    try {
      if (authHubAuth) {
        await firebaseSignOut(authHubAuth).catch(() => {});
      }
      if (auth) {
        await firebaseSignOut(auth);
      }
      if (redirectToHub) {
        const returnUrl = encodeURIComponent(window.location.origin);
        window.location.href = `${this.hubUrl}/logout?return=${returnUrl}`;
      }
    } catch (error) {
      console.error('[AuthHubService] Sign out failed:', error);
      throw error;
    }
  }

  getHubUrl(path = '') {
    return `${this.hubUrl}${path}`;
  }
}

export const authHubService = new AuthHubService();
export default authHubService;
